// crypto_helpers.ts
// Works in modern browsers (WebCrypto) with pako compression

import pako from 'pako';

const enc = new TextEncoder();
const dec = new TextDecoder();

function base64UrlEncode(bytes: Uint8Array) {
	let bin = '';
	bytes.forEach((b) => (bin += String.fromCharCode(b)));
	const b64 = btoa(bin);
	return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function base64UrlDecode(s: string) {
	const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((s.length + 3) % 4);
	const bin = atob(b64);
	const out = new Uint8Array(bin.length);
	for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
	return out;
}

// ✅ IMPORTANT: This key is exposed in FE. Use only if you accept that.
// Test key - hardcoded for testing purposes
const SHARED_KEY_B64URL = 'GWxIdPczfKygWBuigTHF-UN1KWZn4_hXrs9AjUO2I6Y'; // 32 bytes for AES-256

async function importAesKey() {
	const raw = base64UrlDecode(SHARED_KEY_B64URL);
	if (raw.byteLength !== 32) throw new Error('AES key must be 32 bytes (AES-256).');
	return crypto.subtle.importKey('raw', raw, 'AES-GCM', false, ['encrypt', 'decrypt']);
}

export async function encryptJsonToQ(payload: any) {
	const key = await importAesKey();
	const iv = crypto.getRandomValues(new Uint8Array(12)); // 96-bit IV for GCM
	const json = JSON.stringify(payload);

	// ✨ 1. Compress with pako (this reduces size significantly!)
	const compressed = pako.deflate(json);

	// ✨ 2. Convert Uint8Array → Base64 (URL-safe)
	const base64 = btoa(String.fromCharCode(...compressed))
		.replace(/\+/g, '-')
		.replace(/\//g, '_')
		.replace(/=+$/, '');

	// ✨ 3. Encrypt the compressed base64 string
	const cipherBuf = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, enc.encode(base64));

	const cipher = new Uint8Array(cipherBuf);

	// pack: iv + cipher
	const packed = new Uint8Array(iv.length + cipher.length);
	packed.set(iv, 0);
	packed.set(cipher, iv.length);

	return base64UrlEncode(packed);
}

export async function decryptQToJson(q: string) {
	if (!q) return null;
	const key = await importAesKey();

	// 1. Unpack: iv (first 12 bytes) + cipher (rest)
	const packed = base64UrlDecode(q);
	const iv = packed.slice(0, 12);
	const cipher = packed.slice(12);

	// 2. Decrypt → URL-safe base64 string of the compressed payload
	const plainBuf = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, cipher);
	const base64 = dec.decode(plainBuf);

	// 3. base64 → compressed bytes
	const compressed = base64UrlDecode(base64);

	// 4. Inflate and parse JSON
	const json = pako.inflate(compressed, { to: 'string' });
	return JSON.parse(json);
}

